// ----------------------------------------------------------------------
// <copyright file="Izenda.CustomViewPopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * CustomViewPopup component
 */

import React, { Component } from 'react';
import { isNil } from 'lodash/fp';
import connect from 'share/enhancers/Izenda.Connect';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import TextArea from 'share/components/controls/Izenda.TextArea';
import CustomView from 'logic/models/Izenda.CustomView';
import Validator from 'share/components/hoc/Izenda.Validator';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';

const ValidatorInput = Validator()(Input);
const ValidatorSelect = Validator()(Select);
const ValidatorTextArea = Validator()(TextArea);

@connect()
class CustomViewPopup extends Component {
  componentWillMount() {
    const { localization, detail, store } = this.props;

    this.customView = new CustomView(detail);

    this.onChangeDatabase = this.customView.setDatabase.bind(this.customView);
    this.onChangeSchema = this.customView.setSchema.bind(this.customView);
    this.getDatabases = store.getDatabases.bind(store, this.customView);
    this.getSchemas = store.getSchemas.bind(store, this.customView);
    this.title = localization.getText(
      detail ? 'DATA_MODEL_LABEL_EDIT_CUSTOM_VIEW' : 'DATA_MODEL_LABEL_ADD_CUSTOM_VIEW'
    );
    this.constraint = {
      presence: {
        presence: true,
        message: localization.getText('COMMON_CONSTRAINT_REQUIRED')
      }
    };
    this.processingSave = false;
  }

  renderDatabaseName = () => {
    const customView = this.customView;
    const options = customView.databases.length ? customView.databases : [];
    const selectedDatabase = customView.databaseId && {
      key: customView.databaseId,
      value: customView.databaseName
    };

    return (
      <ValidatorSelect
        options={options}
        simpleValue
        searchable={false}
        onChange={this.onChangeDatabase}
        value={selectedDatabase}
        valueKey="key"
        labelKey="value"
        field="database"
        validations={this.constraint}
        isLastPage={customView.isLastPageDatabase}
        getData={this.getDatabases}
        isLazyLoading
      />
    );
  };

  renderSchemaName = () => {
    const customView = this.customView;
    const disabled = !customView.databaseId;
    const options = customView.schemas.length ? customView.schemas : [];
    const selectedSchema = customView.schemaId && {
      key: customView.schemaId,
      value: customView.schemaName
    };

    return (
      <ValidatorSelect
        options={options}
        simpleValue
        searchable={false}
        onChange={this.onChangeSchema}
        value={selectedSchema}
        valueKey="key"
        labelKey="value"
        field="schema"
        validations={this.constraint}
        disabled={disabled}
        isLastPage={customView.isLastPageSchema}
        getData={this.getSchemas}
        isLazyLoading
      />
    );
  };

  saveCustomQuerySource = () => {
    const { store, onAccept, confirmFn, localization } = this.props;

    return store.saveCustomQuerySource(this.customView).then(({ success, messages }) => {
      this.processingSave = false;
      if (isNil(success) || success) {
        store.reload();
        return !this.customView.id
          ? confirmFn('Confirmation', {
            type: 'info',
            description: localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_SAVE_SUCCESSFUL_MESSAGE'),
            showCloseIndicator: true
          }).then(onAccept)
          : onAccept();
      }
      confirmFn('Information', {
        type: 'error',
        description: messages[0].messages[0],
        showCancel: false,
        showCloseIndicator: true
      });

      return null;
    });
  };

  onClosing = (callback, type) => {
    if (!this.processingSave) {
      this.processingSave = true;
      const { confirmFn, store, onReject, validation } = this.props;

      if (type === 'accept') {
        validation.validate().then(result => {
          if (result) {
            store.validateCustomQuerySource(this.customView).then(({ success, confirmation, messages }) => {
              if (success) {
                confirmation
                  ? confirmFn('Confirmation', { description: messages[0] })
                    .then(() => {
                      messages.length > 1
                        ? confirmFn('Confirmation', { description: messages[1] })
                          .then(this.saveCustomQuerySource)
                          .catch(() => { this.processingSave = false; })
                        : this.saveCustomQuerySource();
                    })
                    .catch(() => { this.processingSave = false; })
                  : this.saveCustomQuerySource();
              } else {
                messages[0].key === 'Name'
                  ? validation.updateErrors(messages)
                  : confirmFn('Information', {
                      type: 'error',
                      description: messages[0].messages[0],
                      showCancel: false,
                      showCloseIndicator: true
                    });
                this.processingSave = false;
              }
            });
          }
          else {
            this.processingSave = false;
          }
        });
      } else {
        onReject();
      }
    }
  };

  renderContainer = () => {
    const { localization } = this.props;
    const { query, updateQuery, viewName, updateViewName } = this.customView;

    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            {localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_VIEW_NAME')}
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <ValidatorInput
              type="text"
              field="Name"
              value={viewName}
              onChange={updateViewName}
              validations={this.constraint}
              className="form-control"
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            {localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_DATABASE_NAME')}
          </GridLayout.Col>
          <GridLayout.Col cols={10}>{this.renderDatabaseName()}</GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            {localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_SCHEMA_NAME')}
          </GridLayout.Col>
          <GridLayout.Col cols={10}>{this.renderSchemaName()}</GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols={2}>
            {localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_QUERY')}
          </GridLayout.Col>
          <GridLayout.Col cols={10}>
            <ValidatorTextArea
              rows="15"
              value={query}
              onChange={updateQuery}
              validations={this.constraint}
              className="form-control"
              field="queryString"
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  };

  render() {
    const { localization } = this.props;
    return (
      <Popup visible autoClose={false} onClosing={this.onClosing} type="none" sizeClass="modal-lg">
        <Popup.Header title={this.title} />
        <Popup.Body>
          {this.renderContainer()}
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">
            {localization.getText('DATA_MODEL_LABEL_ADD_CUSTOM_VIEW_SAVE_EXECUTE')}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

export default CustomViewPopup;



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/QuerySourceType/Izenda.CustomViewPopup.js